/* eslint-disable react-refresh/only-export-components -- Lexical registers the node and transformer from the same module as its decorator. */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getNodeByKey,
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
  TextNode,
} from 'lexical';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { PatientDocumentImage } from '@/shared/api/protocol-ts/model/dto_patient_document_pb';

import { patientDocumentMessages } from './config';
import { DocumentImageDisplay } from './DocumentImageDisplay';
import styles from './DocumentImageNode.module.scss';
import { imageViewFilter, imageViewTransform } from './lib';

type SerializedDocumentImageNode = Spread<
  {
    serial: number;
  },
  SerializedLexicalNode
>;

type ImageContextValue = {
  documentID: string;
  images: Map<number, PatientDocumentImage>;
  resolveAsset: (assetID: string) => string;
  updateImage: (
    serial: number,
    caption: string,
    viewOptions?: MedicalImageViewOptions,
  ) => void | Promise<void>;
};

export const DocumentAssetContext = createContext<ImageContextValue>({
  documentID: '',
  images: new Map(),
  resolveAsset: () => '',
  updateImage: () => undefined,
});

const DocumentImage: FC<{
  serial: number;
  nodeKey: NodeKey;
}> = ({ serial, nodeKey }) => {
  const { formatMessage } = useIntl();
  const [editor] = useLexicalComposerContext();
  const { documentID, images, resolveAsset, updateImage } =
    useContext(DocumentAssetContext);
  const image = images.get(serial);
  const [imageFailed, setImageFailed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [captionDraft, setCaptionDraft] = useState('');
  const imageNodeRef = useRef<HTMLSpanElement>(null);

  const caption = image?.Caption ?? '';
  const viewOptions = image?.ViewOptions;
  const src = image?.AssetID ? resolveAsset(image.AssetID) : '';

  useEffect(() => setImageFailed(false), [image]);
  useEffect(() => setCaptionDraft(caption), [caption]);
  useEffect(() => {
    if (!isEditing) {
      return;
    }
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!imageNodeRef.current?.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsEditing(false);
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isEditing]);

  const saveCaption = () => {
    void updateImage(serial, captionDraft);
    setIsEditing(false);
  };

  const remove = () => {
    editor.update(() => $getNodeByKey(nodeKey)?.remove());
  };

  const applyViewOptions = (patch: Partial<MedicalImageViewOptions>) => {
    void updateImage(
      serial,
      captionDraft,
      new MedicalImageViewOptions({
        Rotation: viewOptions?.Rotation ?? 0,
        Zoom: viewOptions?.Zoom ?? 1,
        FlipHorizontal: viewOptions?.FlipHorizontal ?? false,
        Invert: viewOptions?.Invert ?? false,
        Brightness: viewOptions?.Brightness ?? 1,
        Contrast: viewOptions?.Contrast ?? 1,
        ...patch,
      }),
    );
  };

  const move = (direction: -1 | 1) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!$isDocumentImageNode(node)) {
        return;
      }
      const sibling =
        direction === -1 ? node.getPreviousSibling() : node.getNextSibling();
      if (!sibling) {
        return;
      }
      if (direction === -1) {
        sibling.insertBefore(node);
      } else {
        sibling.insertAfter(node);
      }
    });
  };

  if (!image || !src || imageFailed) {
    return (
      <span className={styles.missing}>
        {formatMessage(patientDocumentMessages.imageUnavailable)}
        <button type="button" onClick={remove}>
          {formatMessage(patientDocumentMessages.remove)}
        </button>
      </span>
    );
  }

  return (
    <span
      ref={imageNodeRef}
      className={styles.imageNode}
      data-component="document-image-node"
      contentEditable={false}
    >
      <figure className={styles.imageNodeFigure}>
        <button
          type="button"
          className={styles.imagePreview}
          aria-label={formatMessage(patientDocumentMessages.editImage, {
            caption: caption || formatMessage(patientDocumentMessages.untitled),
          })}
          aria-expanded={isEditing}
          onClick={() => {
            setCaptionDraft(caption);
            setIsEditing((current) => !current);
          }}
        >
          <DocumentImageDisplay
            assetID={image.AssetID}
            documentID={documentID}
            renderFallback={() => (
              <img
                src={src}
                alt={caption || formatMessage(patientDocumentMessages.image)}
                style={{
                  transform: imageViewTransform(viewOptions),
                  filter: imageViewFilter(viewOptions),
                }}
                onError={() => setImageFailed(true)}
              />
            )}
          />
        </button>
        {caption ? <figcaption>{caption}</figcaption> : null}
      </figure>
      <span className={styles.imageEditor} hidden={!isEditing}>
        <label className={styles.captionLabel}>
          {formatMessage(patientDocumentMessages.caption)}
          <input
            className={styles.captionInput}
            type="text"
            value={captionDraft}
            aria-label={formatMessage(patientDocumentMessages.caption)}
            onChange={(event) => setCaptionDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveCaption();
              }
            }}
          />
        </label>
        <span className={styles.viewControls}>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({
                Rotation: ((viewOptions?.Rotation ?? 0) + 90) % 360,
              })
            }
          >
            {formatMessage(patientDocumentMessages.rotate)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({ FlipHorizontal: !viewOptions?.FlipHorizontal })
            }
          >
            {formatMessage(patientDocumentMessages.flip)}
          </button>
          <button
            type="button"
            onClick={() => applyViewOptions({ Invert: !viewOptions?.Invert })}
          >
            {formatMessage(patientDocumentMessages.invert)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({
                Brightness: (viewOptions?.Brightness ?? 1) - 0.1,
              })
            }
          >
            {formatMessage(patientDocumentMessages.dim)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({
                Brightness: (viewOptions?.Brightness ?? 1) + 0.1,
              })
            }
          >
            {formatMessage(patientDocumentMessages.brighten)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({ Contrast: (viewOptions?.Contrast ?? 1) - 0.1 })
            }
          >
            {formatMessage(patientDocumentMessages.lessContrast)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({ Contrast: (viewOptions?.Contrast ?? 1) + 0.1 })
            }
          >
            {formatMessage(patientDocumentMessages.moreContrast)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({ Zoom: (viewOptions?.Zoom ?? 1) * 1.2 })
            }
          >
            {formatMessage(patientDocumentMessages.zoomIn)}
          </button>
          <button
            type="button"
            onClick={() =>
              applyViewOptions({
                Rotation: 0,
                Zoom: 1,
                FlipHorizontal: false,
              })
            }
          >
            {formatMessage(patientDocumentMessages.resetView)}
          </button>
        </span>
        <span className={styles.controls}>
          <button type="button" onClick={saveCaption}>
            {formatMessage(patientDocumentMessages.save)}
          </button>
          <button type="button" onClick={() => move(-1)}>
            {formatMessage(patientDocumentMessages.moveLeft)}
          </button>
          <button type="button" onClick={() => move(1)}>
            {formatMessage(patientDocumentMessages.moveRight)}
          </button>
          <button type="button" onClick={remove}>
            {formatMessage(patientDocumentMessages.remove)}
          </button>
        </span>
      </span>
    </span>
  );
};

export class DocumentImageNode extends DecoratorNode<ReactNode> {
  __serial: number;

  static getType() {
    return 'document-image';
  }

  static clone(node: DocumentImageNode) {
    return new DocumentImageNode(node.__serial, node.__key);
  }

  static importJSON(serializedNode: SerializedDocumentImageNode) {
    return $createDocumentImageNode(serializedNode.serial);
  }

  constructor(serial: number, key?: NodeKey) {
    super(key);
    this.__serial = serial;
  }

  createDOM() {
    const span = document.createElement('span');
    span.className = styles.nodeContainer;
    return span;
  }

  updateDOM() {
    return false;
  }

  exportJSON(): SerializedDocumentImageNode {
    return {
      ...super.exportJSON(),
      type: 'document-image',
      version: 1,
      serial: this.__serial,
    };
  }

  isInline() {
    return true;
  }

  getSerial() {
    return this.__serial;
  }

  decorate() {
    return <DocumentImage serial={this.__serial} nodeKey={this.__key} />;
  }
}

export const $createDocumentImageNode = (serial: number) =>
  new DocumentImageNode(serial);

export const $isDocumentImageNode = (
  node: LexicalNode | null | undefined,
): node is DocumentImageNode => node instanceof DocumentImageNode;

export const DOCUMENT_IMAGE_TRANSFORMER = {
  dependencies: [DocumentImageNode],
  export: (node: LexicalNode) =>
    $isDocumentImageNode(node) ? `![image](image:${node.getSerial()})` : null,
  importRegExp: /!\[([^\]]*)]\(image:(\d+)\)/,
  regExp: /!\[([^\]]*)]\(image:(\d+)\)$/,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    textNode.replace($createDocumentImageNode(Number(match[2])));
  },
  trigger: ')',
  type: 'text-match' as const,
};
